<script setup lang="ts">
/**
 * 保護者が子アカウントの羽根筆を ON にするときの同意（#944）。
 *
 * 読み手は保護者（大人）。何が AI の提供元に送られるか・MP を使うこと・いつでも OFF に戻せることを示し、
 * 確認のチェックを入れたときだけ ON にできる。**文面を変えたら、サーバの `QUILL_CONSENT_VERSION`
 * （`services/guardian_settings.py`）を上げる**（同意した文面の版を子の User に残しているので）。
 */
import LargeDialog from './LargeDialog.vue'

const dialog = defineModel<boolean>()
const props = defineProps<{ childName: string, busy?: boolean }>()
const emit = defineEmits<{ agreed: [] }>()

const { quillMpCost } = storeToRefs(useGameStateStore())
const checked = ref(false)

watch(dialog, (open) => {
  if (open) checked.value = false
})
</script>

<template>
  <LargeDialog
    v-model="dialog"
    title="羽根筆を使えるようにする"
    :cancel-button="{ title: 'やめておく' }"
    :submit-button="{ title: '同意して ON にする', disabled: !checked }"
    keep-open-on-submit
    :busy="props.busy"
    @submit="emit('agreed')"
  >
    <template #default>
      <div
        class="guardian-consent"
        data-testid="guardian-quill-consent"
      >
        <p>
          「羽根筆」は、書きたいことを言葉で伝えると、AI が魔導書のコードを書く機能です。
          {{ props.childName }}さんが使えるようにします。
        </p>
        <ul>
          <li>
            依頼の文と、開いている魔導書のコード（絵や音の名前を含む）が、AI サービスの提供元
            （OpenAI OpCo, LLC〔米国〕・アマゾン ウェブ サービス ジャパン合同会社〔Amazon Bedrock。処理は米国のリージョン〕）に送られます。AI の学習には使われません。
          </li>
          <li>お名前やメールアドレスは送りません（依頼の文に書いた場合を除きます）。</li>
          <li>1 回書いてもらうごとに、魔導エネルギー（MP）を {{ quillMpCost }} 使います。</li>
          <li>書いたものは、変わるところを見てから入れるかを決めます。いつでも OFF に戻せます。</li>
        </ul>
        <p>
          詳しくは<NuxtLink
            to="/docs/privacypolicy"
            target="_blank"
          >プライバシーポリシー</NuxtLink>をご覧ください。
        </p>
        <v-checkbox
          v-model="checked"
          hide-details
          density="comfortable"
          data-testid="guardian-quill-consent-check"
          label="上の内容を確認し、羽根筆を使うことに同意します"
        />
      </div>
    </template>
  </LargeDialog>
</template>

<style scoped>
/* 共有コンポーネントなので calc(N * var(--u))（responsive_sizing.md） */
.guardian-consent {
  font-weight: normal;
  font-size: max(12px, calc(1.6 * var(--u)));
  line-height: 1.7;
  padding: 0 max(8px, calc(1.5 * var(--u)));
}

.guardian-consent ul {
  margin: max(6px, calc(0.8 * var(--u))) 0;
  padding-left: 1.2em;
}

/* 同意のチェックの文も本文と同じ大きさに（Vuetify の既定は小さい） */
.guardian-consent :deep(.v-label) {
  font-size: max(12px, calc(1.6 * var(--u)));
  opacity: 1;
}

.guardian-consent li + li {
  margin-top: max(4px, calc(0.5 * var(--u)));
}
</style>
